/**
 * Resolve participant-facing UI locale for public/anonymous flows.
 * Prefer explicit URL lang (matches Maya API), then session participant locale, then task language.
 */
export function resolveParticipantUiLocale({
  query = {},
  session = null,
  taskLanguage = null,
} = {}) {
  const raw = query.lang
    || query.locale
    || session?.participant_locale
    || taskLanguage;

  if (!raw || typeof raw !== 'string') {
    return 'en';
  }

  const normalized = raw.trim().toLowerCase().replace(/_/g, '-');

  if (normalized === 'pt-pt') {
    return 'pt';
  }

  if (normalized.includes('-')) {
    return normalized.split('-')[0];
  }

  return normalized;
}

/**
 * Resolve locale for logged-in participant portal pages without an active task.
 */
export function resolveParticipantProfileLocale(user = null) {
  const raw = user?.locale
    || user?.language?.value
    || user?.language;

  return resolveParticipantUiLocale({ taskLanguage: raw });
}

/**
 * Portal chrome (nav, footer, dashboard, settings) follows the user's preferred language.
 */
export function resolveParticipantPortalLocale({ user = null, query = {} } = {}) {
  if (query.lang || query.locale) {
    return resolveParticipantUiLocale({ query });
  }

  return resolveParticipantProfileLocale(user);
}

/**
 * Task content follows the task language (with optional URL lang override).
 */
export function resolveParticipantTaskLocale({ query = {}, session = null, task = null } = {}) {
  return resolveParticipantUiLocale({
    query,
    session,
    taskLanguage: task?.language,
  });
}

export const PARTICIPANT_TASK_TRANSLATION_NAMESPACES = new Set([
  'General',
  'Maya',
  'Polls',
  'DiscussionForums',
  'RankingAndSorting',
  'Like',
  'ParticipantMediaUpload',
  'WhatsAppInvite',
  'Misc',
]);

/**
 * Pick the best matching language option value for a participant start screen.
 * @param {{ query?: object, taskLanguage?: string|null, languageOptions?: Array<{ value: string }> }} options
 */
export function resolveDefaultParticipantLanguageValue({
  query = {},
  taskLanguage = null,
  languageOptions = [],
} = {}) {
  const locale = resolveParticipantUiLocale({ query, taskLanguage });

  if (!languageOptions.length) {
    return locale;
  }

  const exact = languageOptions.find((option) => option.value === locale);
  if (exact) {
    return exact.value;
  }

  const prefixed = languageOptions.find((option) => {
    const value = option.value?.toLowerCase() ?? '';

    return value.startsWith(`${locale}-`) || value.startsWith(`${locale}_`);
  });
  if (prefixed) {
    return prefixed.value;
  }

  const taskMatch = languageOptions.find((option) => option.value === taskLanguage);
  if (taskMatch) {
    return taskMatch.value;
  }

  return languageOptions[0]?.value || locale;
}
